import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Clock, Star } from 'lucide-react';
import AreasMap from '@/components/common/AreasMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_ICONS = {
  'dental-implants': '🦷',
  'invisalign': '😁',
  'lanap-gum-treatment': '✨',
  'cosmetic-dentistry': '💎',
  'emergency-dentist': '🚨',
  'general-dentistry': '🏥',
  'teeth-whitening': '✨',
  'porcelain-veneers': '💫',
  'crowns-bridges': '👑',
  'dentures': '😊',
  'root-canals': '🔧',
  'first-fit-veneers': '⭐'
};

const SERVICE_SHORT_NAMES = {
  'dental-implants': 'Implants',
  'invisalign': 'Invisalign',
  'lanap-gum-treatment': 'LANAP',
  'cosmetic-dentistry': 'Cosmetic',
  'emergency-dentist': 'Emergency',
  'general-dentistry': 'General',
  'teeth-whitening': 'Whitening',
  'porcelain-veneers': 'Veneers',
  'crowns-bridges': 'Crowns',
  'dentures': 'Dentures',
  'root-canals': 'Root Canal',
  'first-fit-veneers': 'First Fit Veneers'
};

const AreasWeServe = () => {
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [citiesRes, servicesRes] = await Promise.all([
          fetch(`${API_URL}/api/service-areas/cities`),
          fetch(`${API_URL}/api/service-areas/services`)
        ]);
        
        if (citiesRes.ok) {
          const data = await citiesRes.json();
          setCities(data.cities || []);
        }
        if (servicesRes.ok) {
          const data = await servicesRes.json();
          setServices(data.services || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Group cities by distance
  const nearbyZone = cities.filter(c => c.distance <= 10).sort((a, b) => a.distance - b.distance);
  const midZone = cities.filter(c => c.distance > 10 && c.distance <= 20).sort((a, b) => a.distance - b.distance);
  const extendedZone = cities.filter(c => c.distance > 20 && c.distance <= 30).sort((a, b) => a.distance - b.distance);
  const westernZone = cities.filter(c => c.distance > 30).sort((a, b) => a.distance - b.distance);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental",
    "description": `Award-winning dental care serving Fort Worth and ${cities.length || 53} surrounding cities within the DFW Metroplex. 1,100+ 5-star reviews. Best of Fort Worth 2025.`,
    "url": "https://fortworthdental.com/areas-we-serve",
    "telephone": "(817) 985-3337",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.6823,
      "longitude": -97.4208
    },
    "areaServed": cities.map(city => ({
      "@type": "City",
      "name": city.name + ", Texas"
    })),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1110"
    }
  };

  const CityCard = ({ city }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-800">
            {city.name}
          </h3>
          <p className="text-sm text-gray-500">{city.county} County</p>
        </div>
        <span className="bg-teal-100 text-teal-700 text-xs font-medium px-2 py-1 rounded">
          {city.distance} mi
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {services.map(service => (
          <Link
            key={service.slug}
            to={`/${service.slug}-${city.slug}`}
            className="text-center p-1.5 rounded hover:bg-teal-50 transition group"
            title={`${service.service} in ${city.name}`}
          >
            <span className="text-base block">{SERVICE_ICONS[service.slug] || '🦷'}</span>
            <span className="text-[10px] text-gray-600 group-hover:text-teal-600 block truncate">
              {SERVICE_SHORT_NAMES[service.slug] || service.service}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );

  const CityZone = ({ title, description, cities, bgColor }) => (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-3 h-3 rounded-full ${bgColor}`}></div>
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        <span className="text-gray-500 text-sm">({cities.length} cities)</span>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cities.map(city => (
          <CityCard key={city.slug} city={city} />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Areas We Serve | Dentist Near Me in Fort Worth, Granbury, Weatherford & Surrounding Cities</title>
        <meta name="description" content={`Fort Worth Dental proudly serves ${cities.length || 53} cities including Arlington, Weatherford, Granbury, Cleburne, Glen Rose, Stephenville & more. 1,100+ 5-star reviews. Call (817) 985-3337.`} />
        <meta name="keywords" content="dentist near me, Fort Worth dentist, Granbury dentist, Weatherford dentist, Cleburne dentist, Stephenville dentist, Glen Rose dentist, DFW dental care" />
        <link rel="canonical" href="https://fortworthdental.com/areas-we-serve" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Serving Fort Worth & {cities.length > 0 ? `${cities.length} Surrounding` : 'Surrounding'} Cities
            </h1>
            <p className="text-xl text-teal-100 mb-6">
              Award-winning dental care from DFW to the Hill Country. 
              Join over 1,100+ patients who've given us 5 stars!
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:817-985-3337" 
                className="inline-flex items-center bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                <Phone className="w-5 h-5 mr-2" />
                (817) 985-3337
              </a>
              <Link 
                to="/contact-us" 
                className="inline-flex items-center bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-400 transition border border-teal-400"
              >
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive city map */}
      <AreasMap lang="en" />

      {/* Trust Badges */}
      <section className="bg-gray-50 py-6 border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 items-center text-gray-600">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="font-semibold">1,100+ 5-Star Reviews</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏆</span>
              <span className="font-semibold">Best of Fort Worth 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-600" />
              <span className="font-semibold">Serving DFW Since 1990</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600" />
              <span className="font-semibold">{cities.length > 0 ? `${cities.length} Cities Served` : 'Cities Served'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Service Legend */}
      <section className="py-6 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="font-semibold text-gray-700 mb-3">Click any service icon below to view that service in a specific city</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {services.map(service => (
              <div key={service.slug} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                <span className="text-xl">{SERVICE_ICONS[service.slug] || '🦷'}</span>
                <span className="text-sm text-gray-700">{service.service || SERVICE_SHORT_NAMES[service.slug]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <CityZone
            title="Nearby Cities"
            description="Just minutes away from our office - quick and convenient access to quality dental care."
            cities={nearbyZone}
            bgColor="bg-green-500"
          />
          
          <CityZone
            title="Mid-Range Cities"
            description="A short drive for exceptional dental services worth the trip."
            cities={midZone}
            bgColor="bg-yellow-500"
          />
          
          <CityZone
            title="Extended Service Area"
            description="We proudly serve patients from across the DFW metroplex."
            cities={extendedZone}
            bgColor="bg-blue-500"
          />
          
          {westernZone.length > 0 && (
            <CityZone
              title="Western Texas Communities"
              description="Serving patients from Granbury, Stephenville, and the beautiful Hill Country west of Fort Worth."
              cities={westernZone}
              bgColor="bg-purple-500"
            />
          )}
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Find Us</h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3356.8!2d-97.4208!3d32.6823!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e71c8a8cb1e57%3A0x4c0a8c8c8c8c8c8c!2s4620%20Citylake%20Blvd%20W%2C%20Fort%20Worth%2C%20TX%2076132!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Fort Worth Dental Location"
            ></iframe>
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-2">
              <MapPin className="inline w-5 h-5 text-teal-600 mr-1" />
              4620 Citylake Blvd W, Fort Worth, TX 76132
            </p>
            <a 
              href="https://www.google.com/maps/dir//Fort+Worth+Dental+4620+Citylake+Blvd+W+Fort+Worth+TX+76132"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:text-teal-700 font-semibold"
            >
              Get Directions →
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience Award-Winning Dental Care?</h2>
          <p className="text-xl text-teal-100 mb-8">
            No matter which city you're in, quality dental care is just a short drive away.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:817-985-3337" 
              className="inline-flex items-center justify-center bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
            >
              <Phone className="w-6 h-6 mr-2" />
              Call (817) 985-3337
            </a>
            <Link 
              to="/contact-us" 
              className="inline-flex items-center justify-center bg-teal-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-400 transition border-2 border-teal-400"
            >
              Schedule Online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AreasWeServe;
